import React, { useState, useMemo } from "react";
import { Percent } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

const WinRateMetric = ({ trades = [], userSettings = {} }) => {
  const [isHovering, setIsHovering] = useState(false);

  // Use useMemo to prevent recalculation on every render
  const { stats, donutData } = useMemo(() => {
    const validTrades = trades.filter(
      (trade) =>
        typeof trade.pnl === "number" &&
        typeof trade.entryPrice === "number" &&
        typeof trade.exitPrice === "number"
    );

    if (!validTrades.length) {
      return {
        stats: { wins: 0, losses: 0, breakeven: 0, total: 0, winRate: 0 },
        donutData: [],
      };
    }

    // Log current settings for debugging
    console.log("WinRateMetric - Current settings:", userSettings);

    const stats = validTrades.reduce(
      (acc, trade) => {
        // Determine if this is an options trade based on symbol or instrumentType
        const isOptions =
          (trade.symbol &&
            (trade.symbol.includes("P") || trade.symbol.includes("C"))) ||
          trade.instrumentType === "OPTIONS";

        // Calculate percentReturn based on instrument type and direction
        let percentReturn;
        if (isOptions) {
          // For options, always calculate based on contract price
          percentReturn =
            ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100;
        } else {
          // For futures and stocks, use direction-based calculation
          if (trade.direction === "LONG") {
            percentReturn =
              ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100;
          } else {
            // Short trade calculation
            percentReturn =
              ((trade.entryPrice - trade.exitPrice) / trade.entryPrice) * 100;
          }
        }

        // Get the breakeven threshold, defaulting to 2 if not set
        // Important fix: use exactly the specified threshold value, even if it's 0
        const threshold = userSettings?.customizeBreakeven
          ? parseFloat(userSettings.breakEvenThreshold) || 0
          : 2;

        console.log(
          `Trade: ${trade.symbol}, Return: ${percentReturn.toFixed(
            2
          )}%, Threshold: ${threshold}%, isOptions: ${isOptions}`
        );

        // Check if within breakeven range (absolute value of percentage is less than or equal to threshold)
        if (Math.abs(percentReturn) <= threshold) {
          if (!userSettings?.omitBreakeven) {
            acc.breakeven++;
            acc.totalTrades++;
            console.log(`Classified as breakeven (within ±${threshold}%)`);
          } else {
            console.log(`Omitting breakeven trade (within ±${threshold}%)`);
          }
        } else if (percentReturn > 0) {
          acc.wins++;
          acc.totalTrades++;
          console.log("Classified as win");
        } else {
          acc.losses++;
          acc.totalTrades++;
          console.log("Classified as loss");
        }
        return acc;
      },
      { wins: 0, losses: 0, breakeven: 0, totalTrades: 0 }
    );

    const winRate =
      stats.totalTrades > 0
        ? ((stats.wins / stats.totalTrades) * 100).toFixed(1)
        : 0;

    const finalStats = {
      ...stats,
      total: stats.totalTrades,
      winRate,
    };

    // Create donut data
    const chartData = [];
    if (finalStats.wins > 0) {
      chartData.push({
        name: "Wins",
        value: finalStats.wins,
        color: "#10b981",
      });
    }
    if (finalStats.losses > 0) {
      chartData.push({
        name: "Losses",
        value: finalStats.losses,
        color: "#ef4444",
      });
    }
    if (finalStats.breakeven > 0 && !userSettings?.omitBreakeven) {
      chartData.push({
        name: "Breakeven",
        value: finalStats.breakeven,
        color: "#f59e0b",
      });
    }

    console.log("WinRateMetric stats:", finalStats);

    return { stats: finalStats, donutData: chartData };
  }, [trades, userSettings]);

  return (
    <div className="metric-card">
      <div className="metric-header">
        <h3>Win Rate</h3>
        <Percent size={20} className="metric-icon" />
      </div>
      <div className="win-rate-content">
        <div className="metric-text">
          <span className="metric-value">{stats.winRate}%</span>
          <span className="metric-subtitle">
            {stats.wins} / {stats.total} Trades
            {stats.breakeven > 0 && !userSettings?.omitBreakeven && (
              <span className="breakeven-note" style={{ color: "#f59e0b" }}>
                {}
              </span>
            )}
          </span>
        </div>
        <div
          className={`donut-chart ${isHovering ? "continuous-rotate" : ""}`}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {donutData.length > 0 && (
            <ResponsiveContainer width="100%" height={80}>
              <PieChart>
                <Pie
                  data={donutData}
                  innerRadius={25}
                  outerRadius={35}
                  paddingAngle={2}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  {donutData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(WinRateMetric);
